import type { ExpenseEntry } from './types';

/**
 * Un proveedor, una cifra (revisión de Alex del 14 oct 2026).
 *
 * Carnes Roma decía en la lista «2 vencidas · 341,00 €», en su ficha
 * «Pendiente de pagar 2.253,98 €» y sus facturas sumaban otra cosa. Aquí vive
 * la ÚNICA regla de lo que se debe, la que usan la lista y la ficha de
 * Proveedores, «Por pagar» de Gastos y las cuentas por pagar de la API:
 *
 *  - Se debe un gasto a crédito sin pagar. Una nota de venta ya juntada con su
 *    factura no se debe aparte: la paga su factura.
 *  - Siempre CON IVA, el total real de la factura (`invoiceTotal`, o la base
 *    más el IVA en dinero, o el del desglose): es lo que vas a pagar.
 *  - Vencido = con fecha de vencimiento anterior a hoy.
 *
 * No importa nada de finance ni de receipts: finance la usa y receipts depende
 * de finance, así no hay ciclos.
 */

type GastoPorPagar = Pick<ExpenseEntry, 'paymentType' | 'paid' | 'reconciledInvoiceId' | 'netAmount' | 'vatPct' | 'taxAmount' | 'invoiceTotal' | 'vatBreakdown' | 'dueDate'>;

const redondear = (n: number): number => Math.round(n * 100) / 100;

/** ¿Este gasto está pendiente de pagar? */
export function sePuedeDeber(e: Pick<ExpenseEntry, 'paymentType' | 'paid' | 'reconciledInvoiceId'>): boolean {
  return e.paymentType === 'credito' && !e.paid && !e.reconciledInvoiceId;
}

/** Lo que se paga de un gasto: el total con IVA (igual que `totalDeFactura`). */
export function totalAPagar(e: Pick<ExpenseEntry, 'netAmount' | 'vatPct' | 'taxAmount' | 'invoiceTotal' | 'vatBreakdown'>): number {
  if (e.invoiceTotal != null) return e.invoiceTotal;
  const impuesto = e.taxAmount != null
    ? e.taxAmount
    : e.vatBreakdown?.length
      ? redondear(e.vatBreakdown.reduce((acc, f) => acc + f.tax, 0))
      : Math.round(e.netAmount * (e.vatPct ?? 0)) / 100;
  return redondear(e.netAmount + impuesto);
}

/** ¿Ya venció? Sin fecha de vencimiento, nunca. */
export function estaVencido(e: Pick<ExpenseEntry, 'dueDate'>, hoy: string): boolean {
  return Boolean(e.dueDate && e.dueDate < hoy);
}

export interface ResumenPorPagar {
  /** Todo lo que se debe, vencido o no, con IVA. */
  pendiente: number;
  /** Cuántos gastos se deben. */
  facturas: number;
  /** De eso, lo vencido. */
  vencido: number;
  vencidas: number;
}

/** Lo que se debe de una lista de gastos (de un proveedor, o de todos). */
export function resumenPorPagar(expenses: readonly GastoPorPagar[], hoy: string): ResumenPorPagar {
  let pendiente = 0;
  let facturas = 0;
  let vencido = 0;
  let vencidas = 0;
  for (const e of expenses) {
    if (!sePuedeDeber(e)) continue;
    const total = totalAPagar(e);
    pendiente += total;
    facturas += 1;
    if (estaVencido(e, hoy)) {
      vencido += total;
      vencidas += 1;
    }
  }
  return { pendiente: redondear(pendiente), facturas, vencido: redondear(vencido), vencidas };
}

/** Lo mismo, separado por proveedor (los gastos sin proveedor no entran). */
export function porPagarPorProveedor(expenses: readonly (GastoPorPagar & Pick<ExpenseEntry, 'supplierId'>)[], hoy: string): Map<string, ResumenPorPagar> {
  const grupos = new Map<string, GastoPorPagar[]>();
  for (const e of expenses) {
    if (!e.supplierId || !sePuedeDeber(e)) continue;
    grupos.set(e.supplierId, [...(grupos.get(e.supplierId) ?? []), e]);
  }
  return new Map([...grupos].map(([id, lista]) => [id, resumenPorPagar(lista, hoy)]));
}
